import { useArtis } from "@artis/sdk-react";
import { useEffect, useRef } from "react";

import { signatureContext } from "../formula/complete";
import { selectionToFormula } from "../formula/curves";
import { applyPick, pickSpanAt } from "../formula/picking";
import type { EditSession } from "./useEditSession";

export function useMarketPick(session: EditSession) {
  const artis = useArtis();
  const { draft, draftRef, caretRef, barElRef, cellElRef, focusedEditorRef, highlightTextRef, setDraft } = session;

  // Keep selection state for the current pick burst. The burst is a series of pick events that all apply to the same cell, and it ends when the user edits the formula themselves or closes the edit session. The burst state is updated synchronously in response to grid selection events, so it doesn't lag behind React renders.
  const pickRef = useRef<{ text: string; start: number; refLen: number; caret: number } | null>(null);

  // The text we last wrote, so the reset effect can tell our own updates apart from a real user edit.
  const appliedTextRef = useRef<string | null>(null);
  const editing = draft !== null;

  useEffect(() => {
    if (!artis || !editing) return;
    return artis.v1.grid.selection.subscribe((selection) => {
      const cell = draftRef.current?.cell;
      if (!cell) return;

      const prev = pickRef.current;

      // Continue the burst from our own state; only consult the editor state when starting a fresh pick.
      const text = prev ? prev.text : highlightTextRef.current;
      if (text === null || text === undefined) return;

      const target = prev ? { start: prev.start, end: prev.start + prev.refLen } : pickSpanAt(text, caretRef.current);
      if (!target) return;

      const separator = signatureContext(text, target.start) ? "," : "+";
      const refText = selectionToFormula(selection, separator);
      if (!refText) return;

      const applied = applyPick(text, target, refText);
      pickRef.current = { text: applied.text, start: target.start, refLen: refText.length, caret: applied.caret };
      appliedTextRef.current = applied.text;
      caretRef.current = applied.caret;
      highlightTextRef.current = applied.text;
      setDraft({ cell, text: applied.text });
      const input = focusedEditorRef.current === "cell" ? cellElRef.current : barElRef.current;
      if (input) {
        requestAnimationFrame(() => {
          input.focus();
          input.setSelectionRange(applied.caret, applied.caret);
        });
      }
    });
  }, [artis, editing, highlightTextRef, caretRef, draftRef, focusedEditorRef, cellElRef, barElRef, setDraft]);

  // Close the burst when the user edits the formula themselves or closes the edit session.
  useEffect(() => {
    if (draft === null || draft.text !== appliedTextRef.current) {
      pickRef.current = null;
      appliedTextRef.current = draft?.text ?? null;
    }
  }, [draft]);
}
